// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { Modal } from 'components/atoms/Modal';
import { IconClose } from 'components/atoms/icons/IconClose';
import DOMPurify from 'dompurify';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { createAxios } from 'ts/createAxios';
import { NETWORK_ERROR_MESSAGE } from 'util/ConstantValues';
import { validateRequiredField } from 'util/validator';
import { TextInputFieldLogin } from '../TextInputFieldLogin';
import styles from './CheckPasswordAndConfirmLogoutModal.module.scss';
import { useAuthenLockTime } from 'util/hooks/useAuthenLockTime';

type LogoutInput = {
  password: string;
};

interface Props {
  onClose: () => void;
  onSubmit: () => void;
}

const CheckPasswordAndConfirmLogoutModal: React.FC<Props> = ({
  onClose,
  onSubmit,
}) => {
  const api = createAxios({
    mode: 'login',
  });
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { updateLock, countdown, setCheckLockUserId } = useAuthenLockTime();

  useEffect(() => {
    setCheckLockUserId(user.userId);
  }, [user.userId]);

  // check if the password is correct
  const getErrorMessage = async (userId: string, password: string) => {
    try {
      await api.post('/auth/logout', {
        userId,
        password,
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (err?.response?.data?.endTimeLock) {
        updateLock(userId, err.response.data.endTimeLock);
      }

      // return message when network error
      if (err.message === NETWORK_ERROR_MESSAGE) {
        return t('modal_check_password_and_confirm_logout.network_error');
      }

      // return message when the password is incorrect or the request failed
      return `${t('modal_check_password_and_confirm_logout.input_error')}\n${t(
        'login.message_error'
      )}`;
    }

    return undefined;
  };

  const handleSubmit = async (values: LogoutInput) => {
    setErrorMessage('');
    const errorMessage = await getErrorMessage(user.userId, values.password);

    if (errorMessage) {
      setErrorMessage(errorMessage);
    } else {
      onSubmit();
    }
  };

  return (
    <div className={styles.checkPasswordAndConfirmLogoutModal}>
      <Modal>
        <Form<LogoutInput> onSubmit={handleSubmit}>
          {({ handleSubmit, invalid }) => {
            return (
              <form>
                <div
                  className={styles.checkPasswordAndConfirmLogoutModalContent}
                >
                  <div
                    className={
                      styles.checkPasswordAndConfirmLogoutModalContentIconClose
                    }
                    onClick={onClose}
                  >
                    <IconClose width={16} height={16} />
                  </div>

                  <h3
                    className={
                      styles.checkPasswordAndConfirmLogoutModalContentTitle
                    }
                  >
                    {t('modal_check_password_and_confirm_logout.heading')}
                  </h3>

                  <p
                    className={
                      styles.checkPasswordAndConfirmLogoutModalContentDescription
                    }
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        t(
                          'modal_check_password_and_confirm_logout.content_confirm'
                        ) ?? ''
                      ),
                    }}
                  />

                  <TextInputFieldLogin
                    label={t('login.password') || 'パスワード'}
                    name={'password'}
                    type={'password'}
                    icon="password"
                    placeholder={t('login.password') || 'パスワード'}
                    validate={(password) => {
                      return validateRequiredField(
                        password,
                        t('login.password') || ''
                      )
                        ? (t) =>
                            validateRequiredField(
                              password,
                              t('login.password') || ''
                            )
                        : undefined;
                    }}
                  />
                  {errorMessage && !countdown && (
                    <div
                      className={
                        styles.checkPasswordAndConfirmLogoutModalContentErrors
                      }
                    >
                      {errorMessage}
                    </div>
                  )}

                  {countdown && (
                    <div
                      className={
                        styles.checkPasswordAndConfirmLogoutModalContentErrors
                      }
                    >
                      {t('login.wrong_password_too_many_times')}
                    </div>
                  )}
                </div>

                <div
                  className={styles.checkPasswordAndConfirmLogoutModalFooter}
                >
                  <button
                    type="submit"
                    className={
                      styles.checkPasswordAndConfirmLogoutModalFooterSubmitButton
                    }
                    disabled={invalid || !!countdown}
                    onClick={handleSubmit}
                  >
                    {countdown ||
                      t('modal_check_password_and_confirm_logout.button')}
                  </button>
                </div>
              </form>
            );
          }}
        </Form>
      </Modal>
    </div>
  );
};

export default CheckPasswordAndConfirmLogoutModal;
